import env from 'config';

import { useAuth0, User } from '@auth0/auth0-react';
import useLogout from 'hooks/useLogout';
import { LoginMethod } from 'lib_ts/enums/auth.enums';
import { useCallback, useEffect, useRef } from 'react';
import { refreshTokenHandler } from 'services/_https-client';
import { AuthService } from 'services/auth.service';
import { useAuthStore } from 'stores/use-auth-store';

/**
 * This hook will attempt to resume a login session based on the user's login method.
 *
 * For a user to resume a session they'll always need a refresh token cookie, regardless of login method.
 *
 * Auth0:
 *   1. Confirm the Auth0 session is still valid (getAccessTokenSilently).
 *   2. If we already have a Trajekt Auth0 session, rotate via
 *      /refresh-auth-token and mint a new session id.
 *   3. Otherwise this is first Auth0 login -> POST /loginAuth0 (no refresh
 *      cookie yet).
 *
 * Legacy: rotate via /refresh-auth-token without minting a new session id.
 *
 * While restore is in flight, axios 401s wait instead of starting a second
 * rotate (beginRestore / endRestore on RefreshTokenService).
 */

// Module-level so React Strict Mode's double-mount shares one Auth0 resume.
let auth0ResumeInFlight: Promise<void> | null = null;

const useResumeLogin = () => {
  const {
    user: userAuth0,
    isAuthenticated: auth0IsAuthenticated,
    getAccessTokenSilently,
    isLoading: auth0IsLoading,
  } = useAuth0();

  const { logout } = useLogout();

  const current = useAuthStore((s) => s.current);
  const userAuth0Sub = userAuth0?.sub;

  // Latest Auth0 user without putting the object in resumeAuth0's deps
  // (a new object identity would restart resume on every Auth0 SDK render).
  const userAuth0Ref = useRef(userAuth0);
  userAuth0Ref.current = userAuth0;

  /** AUTH0 - kick-off Auth0 resume by getting a new Auth0 token */
  const resumeAuth0 = useCallback(async () => {
    const processAuth0AccessToken = async (
      accessToken: string,
      auth0User: User
    ) => {
      try {
        useAuthStore.getState().setLoading(true);

        const result = await AuthService.getInstance().loginAuth0(
          accessToken,
          auth0User
        );

        const { success, team, error } = result;

        useAuthStore.getState().setTeam(team);

        if (error?.type === 'verification') {
          useAuthStore.getState().setError(error);
          return;
        }

        if (!success) {
          logout();
          return;
        }

        await useAuthStore.getState().setCurrentSession(success);
      } catch (e) {
        console.error('Auth0 access token error', e);
        logout({
          reason: 'logout',
        });
      } finally {
        useAuthStore.getState().setLoading(false);
      }
    };

    try {
      const FN_NAME = 'getAccessTokenSilently';

      if (!auth0IsAuthenticated) {
        console.debug(`${FN_NAME}: skipped b/c not authenticated`);
        return;
      }

      const auth0User = userAuth0Ref.current;

      if (!auth0User) {
        console.debug(`${FN_NAME}: skipped b/c empty userAuth0`);
        return;
      }

      // Gate only: prove Auth0 is still logged in.
      // Existing Trajekt sessions will rotate with the refresh cookie and pass it to /loginAuth0.
      const newAuth0Token = await getAccessTokenSilently({
        authorizationParams: {
          audience: env.integrations.auth0.audience,
        },
        detailedResponse: true,
      });

      console.debug(
        `${FN_NAME}: updating access token silently to *-${newAuth0Token.access_token.slice(
          -6
        )}`
      );

      const session = useAuthStore.getState().current;
      const hasExistingSession =
        session.auth && session.method === LoginMethod.Auth0;

      if (hasExistingSession) {
        // `true` = new session id on every reload (other features depend on it).
        await useAuthStore.getState().rotateTokens(true);
        return;
      }

      await processAuth0AccessToken(newAuth0Token.access_token, auth0User);
    } catch (e) {
      console.error('resumeAuth0: failed to resume Auth0 session', e);
      logout({
        reason: 'failed-auth0-login',
      });
    }
  }, [auth0IsAuthenticated, userAuth0Sub, getAccessTokenSilently, logout]);

  const shouldResumeAuth0 = auth0IsAuthenticated && !!userAuth0;
  const shouldResumeLegacy =
    current.auth && current.method === LoginMethod.Legacy;

  useEffect(() => {
    if (!shouldResumeAuth0) {
      return;
    }

    // Strict Mode unmounts this effect before the resume finishes
    let cancelled = false;

    const attemptAuth0Resume = async () => {
      useAuthStore.getState().setResumingLogin(true);
      try {
        if (!auth0ResumeInFlight) {
          auth0ResumeInFlight = (async () => {
            refreshTokenHandler.beginRestore();
            try {
              await resumeAuth0();
            } finally {
              refreshTokenHandler.endRestore();
            }
          })().finally(() => {
            auth0ResumeInFlight = null;
          });
        }

        await auth0ResumeInFlight;
      } finally {
        if (!cancelled) {
          useAuthStore.getState().setResumingLogin(false);
        }
      }
    };

    attemptAuth0Resume();

    return () => {
      cancelled = true;
    };
  }, [shouldResumeAuth0, userAuth0Sub, resumeAuth0]);

  useEffect(() => {
    if (!shouldResumeLegacy) {
      return;
    }

    let cancelled = false;

    const attemptLegacyResume = async () => {
      refreshTokenHandler.beginRestore();
      useAuthStore.getState().setResumingLogin(true);
      try {
        await useAuthStore.getState().rotateTokens();
      } finally {
        refreshTokenHandler.endRestore();

        if (!cancelled) {
          useAuthStore.getState().setResumingLogin(false);
        }
      }
    };

    attemptLegacyResume();

    return () => {
      cancelled = true;
    };
  }, [shouldResumeLegacy]);

  const shouldSkipResume =
    !shouldResumeAuth0 && !shouldResumeLegacy && !auth0IsLoading;

  useEffect(() => {
    if (shouldSkipResume) {
      useAuthStore.getState().setResumingLogin(false);
    }
  }, [shouldSkipResume, auth0IsLoading]);
};

export default useResumeLogin;
